import { Fragment, type ReactNode } from 'react'
import EmptyState from './EmptyState'

/**
 * Arquetipo "Tabla" del handoff. Es el más usado del portal: cola global,
 * asesores, conversaciones, candidatos, incidencias, logs, auditoría,
 * usuarios, costos, métricas, mi desempeño.
 *
 * Trae los tres estados que el prototipo no dibuja pero que la pantalla real
 * necesita: cargando (skeleton), vacío, y error con reintento. Sin eso, cada
 * página los improvisa distinto y varias terminan mostrando una tabla vacía
 * cuando en realidad falló la red.
 */

export interface Column<T> {
  key: string
  header: string
  /** Contenido de la celda. Un string se renderiza como texto plano. */
  cell: (row: T) => ReactNode
  /** Subtexto en mono bajo el valor principal (teléfono, id, hora). */
  sub?: (row: T) => string | null
  align?: 'left' | 'right'
  width?: string
}

interface DataTableProps<T> {
  title: string
  subtitle?: string
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  /** Acción a la derecha de la cabecera (botón o pill). */
  action?: ReactNode
  emptyTitle?: string
  emptyMessage?: string
  onRowClick?: (row: T) => void
  /** Fila a destacar, por su `rowKey`. La usa el diagnóstico para llevar a la
   *  incidencia de la que acaba de hablar: sin esto el enlace deja al usuario
   *  mirando la tabla y buscando a ojo cuál era. */
  highlightKey?: string
  /** El grupo de cada fila («Hoy · martes 3 de noviembre»). Cuando cambia de
   *  una fila a la siguiente, va un renglón de encabezado: las filas ya vienen
   *  ordenadas, acá no se reordena nada. Sin esto, la tabla de siempre. */
  grupo?: (row: T) => string
}

export default function DataTable<T>({
  title, subtitle, columns, rows, rowKey,
  loading = false, error = null, onRetry,
  action, emptyTitle = 'Sin resultados', emptyMessage, onRowClick,
  highlightKey = '', grupo,
}: DataTableProps<T>) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-surface-border px-5 py-4">
        <div>
          <h2 className="text-[14px] font-extrabold text-ink">{title}</h2>
          {subtitle && <p className="mt-px text-micro text-ink-muted">{subtitle}</p>}
        </div>
        {action}
      </div>

      {error ? (
        <div className="flex flex-col items-center gap-3 px-5 py-10">
          <p className="text-body text-danger">{error}</p>
          {onRetry && (
            <button onClick={onRetry} className="btn-ghost">Reintentar</button>
          )}
        </div>
      ) : loading ? (
        <TableSkeleton columns={columns.length} />
      ) : rows.length === 0 ? (
        <div className="px-5 py-10">
          <EmptyState title={emptyTitle} description={emptyMessage} />
        </div>
      ) : (
        <div className="scrollbar-thin overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                    className={col.align === 'right' ? '!text-right' : undefined}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <Fragment key={rowKey(row)}>
                {grupo && (i === 0 || grupo(rows[i - 1]) !== grupo(row)) && (
                  <tr>
                    <th colSpan={columns.length} scope="colgroup"
                        className="!bg-surface-alt !text-left text-micro font-extrabold uppercase tracking-wide text-ink-muted">
                      {grupo(row)}
                    </th>
                  </tr>
                )}
                <tr
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={[
                    onRowClick ? 'cursor-pointer' : '',
                    highlightKey && rowKey(row) === highlightKey
                      ? 'bg-brand-light/60 outline outline-1 -outline-offset-1 outline-brand-border'
                      : '',
                  ].filter(Boolean).join(' ') || undefined}
                >
                  {columns.map((col) => {
                    const sub = col.sub?.(row)
                    return (
                      <td key={col.key} className={col.align === 'right' ? 'text-right' : undefined}>
                        {/* El subtexto va DEBAJO del valor (ver `Column.sub`): sin
                            `flex-col` en las columnas a la izquierda quedaba
                            pegado a él en la misma línea («Rojas CamposDNI …»). */}
                        <div className={col.align === 'right' ? 'flex flex-col items-end' : 'flex flex-col'}>
                          <span>{col.cell(row)}</span>
                          {sub && <span className="mt-px font-mono text-meta text-ink-faint">{sub}</span>}
                        </div>
                      </td>
                    )
                  })}
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Skeleton en vez de spinner: mantiene el alto de la tabla y no salta al cargar. */
function TableSkeleton({ columns }: { columns: number }) {
  return (
    <div className="px-5 py-4" aria-busy>
      {Array.from({ length: 6 }).map((_, fila) => (
        <div key={fila} className="flex gap-4 border-b border-surface-subtle py-3 last:border-b-0">
          {Array.from({ length: columns }).map((__, col) => (
            <div
              key={col}
              className="h-3 flex-1 animate-pulse rounded bg-surface-subtle"
              style={{ maxWidth: col === 0 ? '22%' : undefined }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
